'use client';
import { useAtomValue, useAtom } from 'jotai/react';
import { memo, PropsWithChildren, useRef, useState } from 'react';
import { useIntersection } from 'react-use';
import {
  storyAtom,
  maximumReadPercentAtom,
  updateMaximumReadPercentAtomEffect,
} from '~/app/(default-layout)/s/[storyId]/_state/novel-render-atoms';
import { useTellerSession } from '~/common/hooks/use-auth/use-teller-session';
import { useFirebaseAnalytics } from '~/common/hooks/use-firebase-analytics';
import { useTrackSeriesNextStoryToRead } from '~/common/hooks/use-track-series-next-story-to-read';

import { Box } from '@styled-system/jsx';
import { useNovelReadProgress } from './use-novel-read-progress';

type Props = PropsWithChildren<{
  nextStoryId?: string;
  nextStorySeriesIndex?: number;
  nextStoryIsVipOnly?: boolean;
}>;

// Observe whether the elements of OperationItem are shown.
const _ReadEventContainer = ({
  children,
  nextStoryId,
  nextStorySeriesIndex,
  nextStoryIsVipOnly,
}: Props) => {
  const { logEvent } = useFirebaseAnalytics();
  const { session } = useTellerSession();

  const { storeNextStoryToRead, removeStoredSeriesStoryTracking } =
    useTrackSeriesNextStoryToRead();

  const [sentStoryStartRead, setSentStoryStartRead] = useState(false);
  const beginRef = useRef<HTMLDivElement>(null);
  const beginIntersection = useIntersection(beginRef, {
    root: null,
    threshold: 0,
  });

  const maximumReadPercent = useAtomValue(maximumReadPercentAtom);
  const story = useAtomValue(storyAtom);

  // subscribe maximumReadPercent effect
  useAtom(updateMaximumReadPercentAtomEffect);

  // send story_start_read event
  if (
    !sentStoryStartRead &&
    (beginIntersection?.isIntersecting === true || maximumReadPercent > 0) &&
    // Check if the session exists
    session?.uid &&
    story
  ) {
    setSentStoryStartRead(true);

    logEvent('story_start_read', {
      story_id: story.id,
      series_id: story.series?.id,
      like: story.likeCount,
      tags: story.series?.tags ?? [],
      is_official: story.series?.isOfficial ?? false,
    });
  }

  const [sentStoryFinishRead, setSentStoryFinishRead] = useState(false);
  const endRef = useRef<HTMLDivElement>(null);
  const endIntersection = useIntersection(endRef, {
    root: null,
    threshold: 0,
  });
  const { updateReadingProgressOnFinishTrigger, contentRef } =
    useNovelReadProgress({
      story,
      sentStoryFinishRead,
      maximumReadPercentAtom,
    });

  // send story_finish_read event
  if (
    !sentStoryFinishRead &&
    endIntersection?.isIntersecting === true &&
    story &&
    maximumReadPercent === 100 &&
    // Check if the session exists
    session?.uid
  ) {
    setSentStoryFinishRead(true);

    logEvent('story_finish_read', {
      story_id: story.id,
      series_id: story.series?.id,
      tags: story.series?.tags ?? [],
      is_official: story.series?.isOfficial ?? false,
    });

    updateReadingProgressOnFinishTrigger();

    if (story.series?.id && nextStoryId && nextStorySeriesIndex) {
      storeNextStoryToRead(
        story.series.id || '',
        nextStoryId,
        nextStorySeriesIndex,
        nextStoryIsVipOnly ?? false
      );
    } else {
      // If no next story, remove stored series story tracking count
      // So user can start from the beginning again
      removeStoredSeriesStoryTracking();
    }
  }

  return (
    <>
      <Box ref={beginRef} />
      <Box ref={contentRef}>{children}</Box>
      <Box ref={endRef} />
    </>
  );
};

export const ReadEventContainer = memo(_ReadEventContainer);
